import React, { useState, useRef, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getSavedViews,
  getSavedViewsByType,
  createSavedView,
  updateSavedView,
  deleteSavedView,
  SavedViewItem,
  SavedViewFilters,
  ArticlesViewFilters,
  SavedViewType,
  SchemeValue,
  ArticlesSchemeValue,
} from "../../../services/grantsApi";
import { useToast } from "../../../contexts/ToastContext";
import { useFocusTrap } from "../../../hooks/useFocusTrap";
import { isViewDirty, buildSuggestedViewName } from "./utils";
import { hasModuleAccess } from "../../../lib/modules";

/** Must match MAX_SAVED_VIEWS_PER_USER in backend saved-views.constants.ts */
const MAX_VIEWS = 10;

/** Scheme value union covering both contexts (articles adds 'pozostale'). */
type AnySchemeValue = SchemeValue | ArticlesSchemeValue;

interface PresetDef {
  scheme: AnySchemeValue;
  label: string;
  testId: string;
  tooltip: string;
  ariaLabel: string;
}

/** System preset definitions for the CALLS context (KFS · BUR · Wszystkie). */
const CALLS_PRESETS: ReadonlyArray<PresetDef> = [
  {
    scheme: "kfs",
    label: "KFS",
    testId: "preset-pill-kfs",
    tooltip:
      "KFS — nabory Krajowego Funduszu Szkoleniowego (widok systemowy, niemodyfikowalny)",
    ariaLabel:
      "KFS — nabory Krajowego Funduszu Szkoleniowego (widok systemowy, niemodyfikowalny)",
  },
  {
    scheme: "bur",
    label: "BUR",
    testId: "preset-pill-bur",
    tooltip:
      "BUR — nabory Bazy Usług Rozwojowych i programów innych niż KFS (widok systemowy, niemodyfikowalny)",
    ariaLabel:
      "BUR — nabory Bazy Usług Rozwojowych i programów innych niż KFS (widok systemowy, niemodyfikowalny)",
  },
];

/**
 * System preset definitions for the ARTICLES context
 * (KFS · BUR · Pozostałe · Wszystkie order).
 */
const ARTICLES_PRESETS: ReadonlyArray<PresetDef> = [
  {
    scheme: "kfs",
    label: "Aktualności KFS",
    testId: "preset-pill-kfs",
    tooltip:
      "KFS — artykuły powiązane z programem Krajowego Funduszu Szkoleniowego (widok systemowy, niemodyfikowalny)",
    ariaLabel:
      "KFS — artykuły powiązane z programem Krajowego Funduszu Szkoleniowego (widok systemowy, niemodyfikowalny)",
  },
  {
    scheme: "bur",
    label: "Aktualności BUR",
    testId: "preset-pill-bur",
    tooltip:
      "BUR — artykuły powiązane z programami Bazy Usług Rozwojowych (EU) innymi niż KFS (widok systemowy, niemodyfikowalny)",
    ariaLabel:
      "BUR — artykuły powiązane z programami Bazy Usług Rozwojowych (EU) innymi niż KFS (widok systemowy, niemodyfikowalny)",
  },
  {
    scheme: "pozostale",
    label: "Pozostałe",
    testId: "preset-pill-pozostale",
    tooltip:
      "Pozostałe — artykuły bez przypisanego programu KFS ani BUR (źródło bez projektu lub jeszcze nieprzetworzone)",
    ariaLabel:
      "Pozostałe — artykuły bez przypisanego programu KFS ani BUR (źródło bez projektu lub jeszcze nieprzetworzone)",
  },
];

interface SavedViewsBarProps {
  /** Current URL search params (for dirty state detection) */
  currentUrlParams: URLSearchParams;
  /** ID of the currently active view (from ?view= param) */
  activeViewId: string | null;
  /** Called when user clicks a view pill to activate it */
  onViewSelect: (view: SavedViewItem) => void;
  /** Called when user clicks "Wszystkie" to clear filters */
  onClearView: () => void;
  /** Called when ?view= param does not match any existing view (fallback) */
  onViewNotFound?: () => void;
  /** Current filters as object (for auto-suggest name on create) */
  currentFilters: SavedViewFilters | ArticlesViewFilters;
  /** Map of program UUID -> name (for buildSuggestedViewName) */
  programMap?: Record<string, string>;
  /**
   * Called when user selects a scheme preset or clears it (undefined).
   * Parent is responsible for updating URL: set ?scheme=... or remove scheme param.
   * Also clears ?view= param when called.
   *
   * The value is the wide `AnySchemeValue` (kfs|bur|pozostale) because the
   * articles context has a third 'pozostale' bucket. The calls parent
   * (GrantsCalls) therefore narrows/guards against 'pozostale' on its side —
   * a single prop type serves both contexts.
   */
  onSchemeChange?: (scheme: AnySchemeValue | undefined) => void;
  /**
   * Enabled modules for the current tenant (from subscription/capabilities).
   * Used to gate KFS/BUR preset pills — missing module → locked-upsell pill.
   * When undefined, all schemes are treated as accessible (backward compat).
   * Must be passed as props (not read from global context inside the pill).
   *
   * AC-8: grants:kfs gates KFS pill; grants:bur gates BUR pill.
   * grants:* wildcard implies both.
   */
  enabledModules?: string[];
  /**
   * Context discriminator. 'calls' (default) shows KFS · BUR presets and saves
   * calls views; 'articles' shows KFS · BUR · Pozostałe presets and saves
   * articles views.
   */
  viewType?: SavedViewType;
}

/**
 * Toolbar showing saved view tabs above the filter bar.
 *
 * Desktop (>=640px): tab-based toolbar (Shortcut.com-style).
 * Mobile (<640px): native select dropdown.
 *
 * Preset pills (KFS · BUR · Wszystkie) are always visible, independent of
 * user-saved-views loading state. They use placement: "hidden" SchemeFilter
 * plugin for URL serialization.
 *
 * @see docs/specifications/2026-05-27-domyslne-widoki-kfs-bur/spec.md
 * @see docs/specifications/2026-03-04-saved-views.md
 */
const SavedViewsBar: React.FC<SavedViewsBarProps> = ({
  currentUrlParams,
  activeViewId,
  onViewSelect,
  onClearView,
  onViewNotFound,
  currentFilters,
  programMap = {},
  onSchemeChange,
  enabledModules,
  viewType = "calls",
}) => {
  const { addToast } = useToast();
  const queryClient = useQueryClient();

  const isArticles = viewType === "articles";
  const PRESETS = isArticles ? ARTICLES_PRESETS : CALLS_PRESETS;

  // ---- API queries ----
  // Query key is scoped by viewType so calls and articles bars don't share a cache.
  const savedViewsQueryKey = ["grants-saved-views", viewType];
  const {
    data: views = [],
    isSuccess: viewsLoaded,
    isLoading,
    isError,
  } = useQuery<SavedViewItem[]>({
    queryKey: savedViewsQueryKey,
    queryFn: () =>
      // TODO: getSavedViews() and getSavedViewsByType('calls') are equivalent;
      // once the calls tab is fully migrated this branch can collapse to a
      // single getSavedViewsByType(viewType) call. Kept split for now to avoid
      // any behavioural change to the established calls path.
      viewType === "calls" ? getSavedViews() : getSavedViewsByType(viewType),
  });

  // Fallback: if ?view= param does not match any existing view, call onViewNotFound
  useEffect(() => {
    if (!viewsLoaded || !activeViewId || !onViewNotFound) return;
    const found = views.some((v) => v.id === activeViewId);
    if (!found) {
      onViewNotFound();
    }
  }, [viewsLoaded, activeViewId, views, onViewNotFound]);

  const createMutation = useMutation({
    mutationFn: createSavedView,
    onSuccess: (newView) => {
      queryClient.invalidateQueries({ queryKey: savedViewsQueryKey });
      addToast({
        type: "success",
        message: `Widok "${newView.name}" został zapisany.`,
      });
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { message?: string } } })
        ?.response?.data?.message;
      if (msg) {
        addToast({ type: "error", message: msg });
      } else {
        addToast({
          type: "error",
          message: "Nie udało się zapisać widoku. Spróbuj ponownie.",
        });
      }
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: {
        name?: string;
        // Union so saving a dirty articles view keeps articleRoles / scheme.
        filters?: SavedViewFilters | ArticlesViewFilters;
      };
    }) => updateSavedView(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: savedViewsQueryKey });
      addToast({ type: "success", message: "Widok został zaktualizowany." });
    },
    onError: () => {
      addToast({
        type: "error",
        message: "Nie udało się zaktualizować widoku. Spróbuj ponownie.",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteSavedView,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: savedViewsQueryKey });
      addToast({ type: "success", message: "Widok został usunięty." });
    },
    onError: () => {
      addToast({
        type: "error",
        message: "Nie udało się usunąć widoku. Spróbuj ponownie.",
      });
    },
  });

  // ---- Local UI state ----
  const [kebabOpenId, setKebabOpenId] = useState<string | null>(null);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");

  // Create modal state
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [createName, setCreateName] = useState("");

  // Delete confirm modal state
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const renameInputRef = useRef<HTMLInputElement>(null);
  const createInputRef = useRef<HTMLInputElement>(null);
  const createModalRef = useRef<HTMLDivElement>(null);
  const deleteModalRef = useRef<HTMLDivElement>(null);

  useFocusTrap(createModalRef, isCreateModalOpen);
  useFocusTrap(deleteModalRef, confirmDeleteId !== null);

  // Focus rename input when rename starts
  useEffect(() => {
    if (renamingId && renameInputRef.current) {
      renameInputRef.current.focus();
      renameInputRef.current.select();
    }
  }, [renamingId]);

  // Focus create input when modal opens
  useEffect(() => {
    if (isCreateModalOpen && createInputRef.current) {
      createInputRef.current.focus();
      createInputRef.current.select();
    }
  }, [isCreateModalOpen]);

  // Close kebab menu when clicking outside or pressing Escape
  useEffect(() => {
    if (!kebabOpenId) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest("[data-kebab-wrapper]")) {
        setKebabOpenId(null);
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setKebabOpenId(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [kebabOpenId]);

  // ---- Derived state ----
  const activeView = views.find((v) => v.id === activeViewId) ?? null;
  const atLimit = views.length >= MAX_VIEWS;

  // Scheme from URL (for preset active-state detection)
  const currentScheme = currentUrlParams.get("scheme") as AnySchemeValue | null;

  // Active state logic (AC-5):
  // - A preset (KFS/BUR/Pozostałe) is active when: scheme matches AND no ?view= is active
  // - "Wszystkie" is active when: no scheme AND no ?view= is active
  const isPresetActive = (scheme: AnySchemeValue) =>
    activeViewId === null && currentScheme === scheme;
  const isAllActive = activeViewId === null && currentScheme === null;

  // AC-8: Module access gate for KFS/BUR preset pills.
  // When enabledModules is undefined (not passed), treat all schemes as accessible
  // for backward compatibility.
  const isKfsEnabled =
    enabledModules === undefined ||
    hasModuleAccess(enabledModules, "grants:kfs");
  const isBurEnabled =
    enabledModules === undefined ||
    hasModuleAccess(enabledModules, "grants:bur");

  const isDirty =
    activeView !== null && isViewDirty(currentUrlParams, activeView.filters);

  // ---- Handlers ----
  const handleOpenCreateModal = useCallback(() => {
    const suggested = buildSuggestedViewName(currentFilters, programMap);
    setCreateName(suggested);
    setIsCreateModalOpen(true);
  }, [currentFilters, programMap]);

  const handleCreateSubmit = useCallback(() => {
    const trimmed = createName.trim();
    if (!trimmed) return;
    createMutation.mutate(
      // viewType is REQUIRED by the backend discriminated-union schema
      // (CreateSavedViewSchema). It is set from the bar's context prop so the
      // articles bar persists viewType: "articles" and the calls bar "calls".
      { name: trimmed, filters: currentFilters, viewType },
      {
        onSuccess: () => {
          setIsCreateModalOpen(false);
          setCreateName("");
        },
      },
    );
  }, [createName, currentFilters, createMutation, viewType]);

  const handleCreateKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleCreateSubmit();
    } else if (e.key === "Escape") {
      e.preventDefault();
      setIsCreateModalOpen(false);
    }
  };

  const handleSaveDirty = useCallback(() => {
    if (!activeView) return;
    updateMutation.mutate({
      id: activeView.id,
      // currentFilters is already the calls|articles union — no narrowing cast,
      // so articleRoles / scheme='pozostale' survive the save round-trip.
      payload: { filters: currentFilters },
    });
  }, [activeView, currentFilters, updateMutation]);

  const handleStartRename = useCallback((view: SavedViewItem) => {
    setKebabOpenId(null);
    setRenamingId(view.id);
    setRenameValue(view.name);
  }, []);

  const handleRenameCommit = useCallback(() => {
    if (!renamingId) return;
    const trimmed = renameValue.trim();
    if (!trimmed) {
      setRenamingId(null);
      return;
    }
    const view = views.find((v) => v.id === renamingId);
    if (view && trimmed !== view.name) {
      updateMutation.mutate({ id: renamingId, payload: { name: trimmed } });
    }
    setRenamingId(null);
  }, [renamingId, renameValue, views, updateMutation]);

  const handleRenameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleRenameCommit();
    } else if (e.key === "Escape") {
      e.preventDefault();
      setRenamingId(null);
    }
  };

  const handleOpenDeleteConfirm = useCallback((viewId: string) => {
    setKebabOpenId(null);
    setConfirmDeleteId(viewId);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (!confirmDeleteId) return;
    deleteMutation.mutate(confirmDeleteId, {
      onSuccess: () => {
        setConfirmDeleteId(null);
      },
    });
  }, [confirmDeleteId, deleteMutation]);

  // Handle keyboard on pill divs
  const handlePillKeyDown = useCallback(
    (e: React.KeyboardEvent, action: () => void) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        action();
      }
    },
    [],
  );

  // ---- Preset handler ----
  const handlePresetClick = useCallback(
    (scheme: AnySchemeValue) => {
      // Parent's onSchemeChange atomically clears ?view= and sets ?scheme=
      // (preserving other filters per spec DD-1), so we don't call
      // onClearView() here. Single navigation = no race condition.
      if (onSchemeChange) {
        onSchemeChange(scheme);
      }
    },
    [onSchemeChange],
  );

  const handleAllClick = useCallback(() => {
    // Clear scheme (back to "Wszystkie") and clear any active view
    if (onSchemeChange) {
      onSchemeChange(undefined);
    }
    onClearView();
  }, [onSchemeChange, onClearView]);

  // ---- Mobile select handler ----
  // Preset option values use the "__scheme__" prefix (e.g. "__kfs__").
  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    if (value === "__all__") {
      handleAllClick();
      return;
    }
    const presetMatch = PRESETS.find((p) => value === `__${p.scheme}__`);
    if (presetMatch) {
      handlePresetClick(presetMatch.scheme);
      return;
    }
    const view = views.find((v) => v.id === value);
    if (view) onViewSelect(view);
  };

  // Derive current mobile select value
  const mobileSelectValue = (() => {
    if (activeViewId !== null) return activeViewId;
    if (currentScheme && PRESETS.some((p) => p.scheme === currentScheme)) {
      return `__${currentScheme}__`;
    }
    return "__all__";
  })();

  const confirmDeleteView = views.find((v) => v.id === confirmDeleteId);

  // ---- Render ----
  return (
    <>
      {/* Desktop toolbar (hidden on mobile) */}
      <div
        role="toolbar"
        aria-label="Zapisane widoki"
        data-testid="saved-views-bar"
        data-tour="calls-saved-views"
        className="hidden sm:flex items-center gap-0 border-b border-neutral-200"
      >
        {/* Preset pills: KFS · BUR (before "Wszystkie" — DD-4 ordering) */}
        {PRESETS.map((preset) => {
          const isActive = isPresetActive(preset.scheme);
          const isSchemeEnabled = preset.scheme === "kfs" ? isKfsEnabled : isBurEnabled;

          // AC-8: locked-upsell pill when module is missing — not hidden (Ustalenie #9)
          if (!isSchemeEnabled) {
            return (
              <Link
                key={preset.scheme}
                to="/tenant/subscription"
                data-testid={`locked-pill-${preset.scheme}`}
                title={`${preset.label} — moduł niedostępny w Twoim planie. Kliknij aby odblokować.`}
                aria-label={`${preset.label} — moduł niedostępny w Twoim planie. Kliknij aby odblokować.`}
                className="inline-flex items-center gap-1.5 px-4 py-3 -mb-px text-sm font-medium select-none transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 rounded-t-md border-b-2 border-b-transparent text-neutral-400 hover:text-neutral-600 hover:bg-neutral-50 cursor-pointer"
              >
                {/* Lock icon */}
                <span aria-hidden="true" className="text-neutral-400">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="w-3.5 h-3.5">
                    <path fillRule="evenodd" d="M8 1a3.5 3.5 0 0 0-3.5 3.5V7A1.5 1.5 0 0 0 3 8.5v4A1.5 1.5 0 0 0 4.5 14h7a1.5 1.5 0 0 0 1.5-1.5v-4A1.5 1.5 0 0 0 11 7V4.5A3.5 3.5 0 0 0 8 1Zm2 6V4.5a2 2 0 1 0-4 0V7h4Z" clipRule="evenodd" />
                  </svg>
                </span>
                {preset.label}
                <span className="text-xs text-neutral-400 ml-0.5">Odblokuj</span>
              </Link>
            );
          }

          return (
            <div
              key={preset.scheme}
              role="button"
              tabIndex={0}
              data-testid={preset.testId}
              data-active={String(isActive)}
              aria-pressed={isActive}
              aria-label={preset.ariaLabel}
              title={preset.tooltip}
              onClick={() => handlePresetClick(preset.scheme)}
              onKeyDown={(e) =>
                handlePillKeyDown(e, () => handlePresetClick(preset.scheme))
              }
              className={`inline-flex items-center gap-1.5 px-4 py-3 -mb-px text-sm font-medium cursor-pointer select-none transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 rounded-t-md ${
                isActive
                  ? "border-b-2 border-b-primary-600 bg-primary-50 text-primary-700"
                  : "border-b-2 border-b-transparent text-neutral-500 hover:text-neutral-700 hover:bg-neutral-50"
              }`}
            >
              {/* System preset badge (visual distinction from user views) */}
              <span
                aria-hidden="true"
                className="inline-flex items-center justify-center w-4 h-4 text-[10px] font-bold rounded bg-neutral-200 text-neutral-600"
                title="Widok systemowy"
              >
                S
              </span>
              {preset.label}
            </div>
          );
        })}

        {/* "Wszystkie" tab */}
        <div
          role="button"
          tabIndex={0}
          data-testid="saved-view-pill-all"
          data-active={String(isAllActive)}
          aria-pressed={isAllActive}
          title={
            isArticles
              ? "Wszystkie artykuły — brak ograniczeń programowych"
              : "Wszystkie nabory — brak ograniczeń programowych"
          }
          onClick={handleAllClick}
          onKeyDown={(e) => handlePillKeyDown(e, handleAllClick)}
          className={`inline-flex items-center px-4 py-3 -mb-px text-sm font-medium cursor-pointer select-none transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 rounded-t-md ${
            isAllActive
              ? "border-b-2 border-b-primary-600 bg-primary-50 text-primary-700"
              : "border-b-2 border-b-transparent text-neutral-500 hover:text-neutral-700 hover:bg-neutral-50"
          }`}
        >
          Wszystkie
        </div>

        {isLoading && (
          <div className="inline-flex items-center px-3 py-1.5 text-sm text-neutral-400 animate-pulse">
            Ładowanie widoków...
          </div>
        )}
        {isError && (
          <div className="inline-flex items-center px-3 py-1.5 text-sm text-red-500">
            Błąd ładowania widoków
          </div>
        )}

        {/* Saved view tabs */}
        {views.map((view) => {
          const isActive = view.id === activeViewId;
          const viewDirty = isActive && isDirty;

          return (
            <div key={view.id} className="inline-flex items-center">
              {/* The tab itself */}
              <div
                className={`group relative inline-flex items-center gap-1.5 px-4 py-3 -mb-px text-sm font-medium cursor-pointer select-none transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 rounded-t-md ${
                  isActive
                    ? "border-b-2 border-b-primary-600 bg-primary-50 text-primary-700"
                    : "border-b-2 border-b-transparent text-neutral-500 hover:text-neutral-700 hover:bg-neutral-50"
                }`}
                role="button"
                tabIndex={0}
                data-testid="saved-view-pill"
                data-view-id={view.id}
                data-active={String(isActive)}
                data-dirty={String(viewDirty)}
                aria-pressed={isActive}
                onClick={() => {
                  if (renamingId === view.id) return;
                  onViewSelect(view);
                }}
                onKeyDown={(e) => {
                  if (renamingId === view.id) return;
                  handlePillKeyDown(e, () => onViewSelect(view));
                }}
              >
                {/* Name - inline rename or display */}
                {renamingId === view.id ? (
                  <input
                    ref={renameInputRef}
                    data-testid="view-rename-input"
                    type="text"
                    value={renameValue}
                    maxLength={100}
                    onChange={(e) => setRenameValue(e.target.value)}
                    onBlur={handleRenameCommit}
                    onKeyDown={handleRenameKeyDown}
                    className="bg-transparent border-b border-current outline-none w-24 text-sm"
                    onClick={(e) => e.stopPropagation()}
                  />
                ) : (
                  <span className={viewDirty ? "italic" : ""}>{view.name}</span>
                )}

                {/* Kebab trigger - visible on hover/focus-within */}
                {renamingId !== view.id && (
                  <div
                    data-kebab-wrapper={view.id}
                    className="opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 [@media(hover:none)]:opacity-100 relative"
                  >
                    <button
                      type="button"
                      data-testid="view-kebab-menu"
                      aria-label={`Opcje widoku ${view.name}`}
                      aria-haspopup="menu"
                      aria-expanded={kebabOpenId === view.id}
                      className="w-5 h-5 flex items-center justify-center rounded hover:bg-neutral-200 focus:outline-none focus:ring-1 focus:ring-neutral-400"
                      onClick={(e) => {
                        e.stopPropagation();
                        setKebabOpenId(
                          kebabOpenId === view.id ? null : view.id,
                        );
                      }}
                    >
                      <span
                        aria-hidden="true"
                        className="text-base leading-none"
                      >
                        ⋮
                      </span>
                    </button>

                    {/* Context menu */}
                    {kebabOpenId === view.id && (
                      <div
                        role="menu"
                        className="absolute right-0 top-full mt-1 z-50 min-w-[130px] bg-white border border-neutral-200 rounded-md shadow-lg py-1"
                      >
                        <div
                          role="menuitem"
                          data-testid="view-rename-option"
                          tabIndex={0}
                          className="px-3 py-2 text-sm text-neutral-700 hover:bg-neutral-100 cursor-pointer focus:outline-none focus:bg-neutral-100"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleStartRename(view);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              handleStartRename(view);
                            }
                          }}
                        >
                          Zmień nazwę
                        </div>
                        <div
                          role="menuitem"
                          data-testid="view-delete-option"
                          tabIndex={0}
                          className="px-3 py-2 text-sm text-red-600 hover:bg-red-50 cursor-pointer focus:outline-none focus:bg-red-50"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenDeleteConfirm(view.id);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              handleOpenDeleteConfirm(view.id);
                            }
                          }}
                        >
                          Usuń
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Save dirty button - OUTSIDE the tab */}
              {viewDirty && (
                <button
                  type="button"
                  data-testid="save-dirty-btn"
                  onClick={handleSaveDirty}
                  disabled={updateMutation.isPending}
                  className="inline-flex items-center justify-center w-6 h-6 ml-1 rounded bg-primary-100 text-primary-700 hover:bg-primary-200 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors"
                  title="Zapisz zmiany w widoku"
                  aria-label="Zapisz zmiany w widoku"
                >
                  {/* Checkmark icon */}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className="w-4 h-4"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              )}
            </div>
          );
        })}

        {/* Add view button */}
        <button
          type="button"
          data-testid="add-saved-view-btn"
          onClick={handleOpenCreateModal}
          disabled={atLimit}
          title={
            atLimit
              ? "Masz już 10 zapisanych widoków. Usuń jeden, żeby dodać nowy."
              : "Dodaj nowy widok"
          }
          aria-label={
            atLimit
              ? "Masz już 10 zapisanych widoków. Usuń jeden, żeby dodać nowy."
              : "Dodaj nowy widok"
          }
          className={`inline-flex items-center justify-center px-3 py-3 -mb-px text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 border-b-2 border-b-transparent rounded-t-md ${
            atLimit
              ? "text-neutral-300 cursor-not-allowed"
              : "text-neutral-400 hover:bg-neutral-50 hover:text-neutral-600"
          }`}
        >
          + Widok
        </button>
      </div>

      {/* Mobile view: native select (visible only on mobile) */}
      <div
        data-tour="calls-saved-views"
        className="sm:hidden flex items-center gap-2 px-4 py-3 border-b border-neutral-200"
      >
        <label
          htmlFor="saved-views-select"
          className="text-sm text-neutral-600 whitespace-nowrap"
        >
          Widok:
        </label>
        <select
          id="saved-views-select"
          value={mobileSelectValue}
          onChange={handleSelectChange}
          className="flex-1 text-sm border border-neutral-300 rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          {/* System presets group (AC-9) */}
          {/* AC-8: disabled option for locked schemes (iOS ignores disabled styling but blocks selection) */}
          <optgroup label="Presety systemowe">
            {PRESETS.map((preset) => {
              const isSchemeEnabled =
                preset.scheme === "kfs" ? isKfsEnabled : isBurEnabled;
              return (
                <option
                  key={preset.scheme}
                  value={`__${preset.scheme}__`}
                  disabled={!isSchemeEnabled}
                >
                  {isSchemeEnabled
                    ? preset.label
                    : `${preset.label} — Odblokuj w subskrypcji`}
                </option>
              );
            })}
          </optgroup>
          {/* "All" option */}
          <option value="__all__">Wszystkie</option>
          {/* User views group */}
          {views.length > 0 && (
            <optgroup label="Moje widoki">
              {views.map((view) => {
                const isActive = view.id === activeViewId;
                const dirty = isActive && isDirty;
                return (
                  <option key={view.id} value={view.id}>
                    {view.name}
                    {dirty ? " (zmieniony)" : ""}
                  </option>
                );
              })}
            </optgroup>
          )}
        </select>

        {/* AC-8: Mobile locked-CTA below select (iOS awareness — disabled options unreliable on iOS) */}
        {(!isKfsEnabled || !isBurEnabled) && (
          <Link
            to="/tenant/subscription"
            data-testid="mobile-locked-cta"
            className="text-xs text-primary-600 hover:text-primary-700 underline whitespace-nowrap"
          >
            {!isKfsEnabled && !isBurEnabled
              ? "Odblokuj KFS i BUR"
              : !isKfsEnabled
                ? "Odblokuj KFS"
                : "Odblokuj BUR"}
          </Link>
        )}

        {/* Save dirty button (mobile) */}
        {isDirty && activeView && (
          <button
            type="button"
            data-testid="save-dirty-btn-mobile"
            onClick={handleSaveDirty}
            disabled={updateMutation.isPending}
            title="Zapisz zmiany"
            aria-label="Zapisz zmiany w widoku"
            className="inline-flex items-center justify-center w-8 h-8 rounded-md bg-primary-100 text-primary-700 hover:bg-primary-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              className="w-4 h-4"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        )}

        {/* Add button (mobile) */}
        <button
          type="button"
          data-testid="add-saved-view-btn-mobile"
          onClick={handleOpenCreateModal}
          disabled={atLimit}
          title={
            atLimit
              ? "Masz już 10 zapisanych widoków. Usuń jeden, żeby dodać nowy."
              : "Dodaj nowy widok"
          }
          aria-label={
            atLimit
              ? "Masz już 10 zapisanych widoków. Usuń jeden, żeby dodać nowy."
              : "Dodaj nowy widok"
          }
          className={`inline-flex items-center justify-center w-8 h-8 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 ${
            atLimit
              ? "text-neutral-300 cursor-not-allowed"
              : "text-neutral-500 hover:bg-neutral-100"
          }`}
        >
          +
        </button>
      </div>

      {/* Create modal */}
      {isCreateModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          role="presentation"
          onClick={(e) => {
            if (e.target === e.currentTarget) setIsCreateModalOpen(false);
          }}
        >
          <div
            ref={createModalRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="save-view-title"
            data-testid="save-view-modal"
            className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4 p-6"
          >
            <h2
              id="save-view-title"
              className="text-lg font-semibold text-neutral-900 mb-4"
            >
              Zapisz widok
            </h2>
            <div className="mb-4">
              <label
                htmlFor="save-view-name"
                className="block text-sm font-medium text-neutral-700 mb-1"
              >
                Nazwa widoku
              </label>
              <input
                ref={createInputRef}
                id="save-view-name"
                type="text"
                data-testid="save-view-name-input"
                value={createName}
                maxLength={100}
                onChange={(e) => setCreateName(e.target.value)}
                onKeyDown={handleCreateKeyDown}
                placeholder="np. FERS, Mazowsze, Otwarte"
                className="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                data-testid="save-view-cancel-btn"
                onClick={() => setIsCreateModalOpen(false)}
                className="px-4 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-md hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                Anuluj
              </button>
              <button
                type="button"
                onClick={handleCreateSubmit}
                disabled={!createName.trim() || createMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                {createMutation.isPending ? "Zapisywanie..." : "Zapisz"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      {confirmDeleteId && confirmDeleteView && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          role="presentation"
          onClick={(e) => {
            if (e.target === e.currentTarget) setConfirmDeleteId(null);
          }}
        >
          <div
            ref={deleteModalRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-view-title"
            data-testid="view-delete-confirm"
            className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4 p-6"
          >
            <h2
              id="delete-view-title"
              className="text-lg font-semibold text-neutral-900 mb-2"
            >
              Usuń widok
            </h2>
            <p className="text-sm text-neutral-600 mb-6">
              Czy na pewno chcesz usunąć widok{" "}
              <span className="font-medium">
                &ldquo;{confirmDeleteView.name}&rdquo;
              </span>
              ?
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                data-testid="view-delete-cancel-btn"
                onClick={() => setConfirmDeleteId(null)}
                className="px-4 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-md hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                Anuluj
              </button>
              <button
                type="button"
                onClick={handleConfirmDelete}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                {deleteMutation.isPending ? "Usuwanie..." : "Usuń"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SavedViewsBar;
